import { Interval } from "@digits-graphql/admin/graphql-bearer"
import {
  createRoutes,
  type RouteConfig,
  StaticRoutes,
} from "@digits-shared/components/Router/DigitsRoute"

class AdminStaticRoutes extends StaticRoutes {
  root = "/"
  login = "/login"
  logout = "/logout"

  userDetails = "/users/:userId"
  signUpDetails = "/signups/:signupId"

  organizationDetails: RouteConfig = {
    parameterizedPath: "/organizations/:organizationId",
    moduleName: "Organization Details",
  }

  organizationSupport: RouteConfig = {
    parentRoute: this.organizationDetails,
    parameterizedPath: "/support",
    moduleName: "Organization Support",
  }

  organizationEmployees: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/employees",
    moduleName: "Organization Employees",
  }

  organizationBilling: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/billing",
    moduleName: "Organization Billing",
  }

  organizationMaintenance: RouteConfig = {
    parentRoute: this.organizationSupport,
    parameterizedPath: "/maintenance",
    moduleName: "Organization Maintenance",
  }

  legalEntityDetails: RouteConfig = {
    parameterizedPath: "/legal-entities/:legalEntityId",
    moduleName: "Legal Entity Details",
  }

  legalEntityParties: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/parties",
    moduleName: "Legal Entity Parties",
  }

  legalEntityPartiesByTypeAndDesignation: RouteConfig = {
    parentRoute: this.legalEntityParties,
    parameterizedPath: "/type/:type/designation/:designation",
    moduleName: "Legal Entity Parties",
  }

  legalEntityPrefixes: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/prefixes",
    moduleName: "Legal Entity Prefixes",
  }

  legalEntityPrefixesPotential: RouteConfig = {
    parentRoute: this.legalEntityPrefixes,
    parameterizedPath: "/potential",
    moduleName: "Legal Entity Prefixes - Potential",
  }

  legalEntityPrefixesConfirmed: RouteConfig = {
    parentRoute: this.legalEntityPrefixes,
    parameterizedPath: "/confirmed",
    moduleName: "Legal Entity Prefixes - Confirmed",
  }

  legalEntitySupport: RouteConfig = {
    parentRoute: this.legalEntityDetails,
    parameterizedPath: "/support",
    moduleName: "Legal Entity Support",
  }

  legalEntityValidation: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/validation",
    moduleName: "Legal Entity Validation",
  }

  legalEntityEmployees: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/employees",
    moduleName: "Legal Entity Employees",
  }

  legalEntityBookClosed: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/book-closed",
    moduleName: "Legal Entity Books Closed",
  }

  legalEntityMaintenance: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/maintenance",
    moduleName: "Legal Entity Maintenance",
  }

  legalEntityDataSourceJobErrors: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/datasourcejob/errors",
    moduleName: "Legal Entity Datasource Errors",
  }

  legalEntityReports: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/reports",
    moduleName: "Legal Entity Reports",
    timeParameterOptions: {
      interval: Interval.Month,
    },
  }

  legalEntityReportValidation: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/report/validation/:reportId/:versionId",
    moduleName: "Legal Entity Report Validation",
  }

  legalEntityConnections: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/connections",
    moduleName: "Legal Entity Connections",
  }

  legalEntityConnection: RouteConfig = {
    parentRoute: this.legalEntityConnections,
    parameterizedPath: "/:id",
    moduleName: "Legal Entity Connection",
  }

  legalEntityCategories: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/categories",
    moduleName: "Legal Entity Categories",
  }

  legalEntityQBOLineClasses: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/qbo-lineclass",
    moduleName: "Legal Entity QBO Line Classes",
  }

  legalEntityPayrollProviders: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/payroll-providers",
    moduleName: "Legal Entity Payroll Providers",
  }

  legalEntityAgentParties: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/agent-parties",
    moduleName: "Legal Entity Agent Parties",
  }

  legalEntityPartyExtractions: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/party-extractions",
    moduleName: "Legal Entity Party Extractions",
  }

  legalEntityCSVImports: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/csv-imports",
    moduleName: "Legal Entity CSV Imports",
  }

  legalEntityQBODepartments: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/qbo-departments",
    moduleName: "Legal Entity QBO Departments",
  }

  legalEntityReconciliations: RouteConfig = {
    parentRoute: this.legalEntitySupport,
    parameterizedPath: "/reconciliations",
    moduleName: "Legal Entity Reconciliations",
  }

  additionalServices = "/additional-services"

  plgOnboarding = `${this.additionalServices}/plg-onboarding`
  plgOnboardingStage = `${this.additionalServices}/plg-onboarding/:stage`
  firmOnboarding = `${this.additionalServices}/firm-onboarding`
  firmOnboardingStage = `${this.additionalServices}/firm-onboarding/:stage`

  legalEntities = `${this.additionalServices}/legal-entities`
  legalEntitiesByStatus = `${this.additionalServices}/legal-entities/:status`

  organizations = `${this.additionalServices}/organizations`
  accountantOrganizations = `${this.organizations}/accountants`
  sandboxOrganizations = `${this.organizations}/sandboxes`
  developerOrganizations = `${this.organizations}/developers`
  allOrganizations = `${this.organizations}/all`

  parties = `${this.additionalServices}/parties`
  partiesGlobal = `${this.parties}/global`
  partiesPrefixes = `${this.parties}/prefixes`
  partiesPrefixesPotential = `${this.partiesPrefixes}/potential`
  partiesPrefixesConfirmed = `${this.parties}/prefixes/confirmed`
  partiesPrefixesIgnored = `${this.parties}/prefixes/ignored`

  institutions = `${this.additionalServices}/institutions`
  institutionDetails = `${this.institutions}/:id`

  doppelgangerPermits = `${this.additionalServices}/permits`

  transactions = `${this.additionalServices}/transactions`
  transactionsDetails = `${this.transactions}/:id`

  workItems = `${this.additionalServices}/work-items`
  workItemDetails = `${this.workItems}/:id`

  sandbox = `${this.additionalServices}/sandbox`

  superusers = `${this.additionalServices}/superusers`

  design = `${this.additionalServices}/design`

  emailTemplates = `${this.additionalServices}/emailTemplates`
  emailTemplate = `${this.emailTemplates}/:template`

  chartsOfAccounts = `${this.additionalServices}/coa`
  chartsOfAccountsChart = `${this.chartsOfAccounts}/:id`
  chartsOfAccountsTemplate = `${this.chartsOfAccounts}/:templateId/:organizationId`

  validation = `${this.additionalServices}/validation`
  validationStats = `${this.validation}/stats`

  databaseMigrations = `${this.additionalServices}/database-migrations`
  databaseMigration = `${this.databaseMigrations}/:prNumber`

  emailPreferences = `${this.additionalServices}/email-preferences`

  localTools = `${this.additionalServices}/local-tools`
  quickbooksXmlViewer = `${this.localTools}/quickbooks-xml-viewer`

  machineLearning = `${this.additionalServices}/machine-learning`
  embeddingSearch = `${this.additionalServices}/embedding-search`
  connect = `${this.additionalServices}/connect`
  partners = `${this.additionalServices}/partners`
  tierDetails = `${this.partners}/:stripeId`

  documents = `${this.additionalServices}/documents`

  pipelineDefinitions = `${this.additionalServices}/pipeline-definitions`
}

export default createRoutes(AdminStaticRoutes)
